// ──────────────────────────────────────────────────────────────────────────
//  Processing-batch bulk import — server adapter (v2 template).
//  New columns: Purchase Date, Grade, Season, Process Date, Process Type,
//  Parchment (Kg), Bags, Vehicle Plate, Harvested, Pulped, Washed,
//  Drying Started, Moved to Store.
// ──────────────────────────────────────────────────────────────────────────

export const BULK_RECORD_STATUS = {
  SUCCESS: "SUCCESS",
  FAILED: "FAILED",
  DATA_DISCREPANCY: "DATA_DISCREPANCY",
};
BULK_RECORD_STATUS.ISSUES_DETECTED = BULK_RECORD_STATUS.DATA_DISCREPANCY;

export function isDataDiscrepancyRecord(record) {
  const s = String(record?.status || "").toUpperCase();
  return s === BULK_RECORD_STATUS.DATA_DISCREPANCY || s === "ISSUES_DETECTED";
}
export function isSuccessRecord(record) {
  return String(record?.status || "").toUpperCase() === BULK_RECORD_STATUS.SUCCESS;
}
export function isFailedRecord(record) {
  return String(record?.status || "").toUpperCase() === BULK_RECORD_STATUS.FAILED;
}

export const SERVER_RECORD_STATUS = {
  SUCCESS: "SUCCESS",
  ERROR_DETECTED: "ERROR_DETECTED",
  DELETED: "DELETED",
};

export const SERVER_BULK_IMPORT_STATUS = {
  VALIDATING: "VALIDATING",
  SUCCESS: "SUCCESS",
  CANCELLED: "CANCELLED",
};

/** API error code → i18n key for field-level messages / tooltips. */
export const SERVER_ERROR_CODE_I18N = {
  PURCHASE_DATE_REQUIRED:   "errorCodePurchaseDateRequired",
  INVALID_PURCHASE_DATE:    "errorCodeInvalidPurchaseDate",
  PROCESS_DATE_REQUIRED:    "errorCodeProcessDateRequired",
  INVALID_PROCESS_DATE:     "errorCodeInvalidProcessDate",
  UNKNOWN_PROCESS_TYPE:     "errorCodeUnknownProcessType",
  NO_ORDERS_FOR_DATE:       "errorCodeNoOrdersForDate",
  PARCHMENT_INVALID:        "errorCodeParchmentInvalid",
  PARCHMENT_EXCEEDS_CHERRY: "errorCodeParchmentExceedsCherry",
  INVALID_DATA:             "errorCodeInvalidData",
};

/** API error code → i18n key for short filter-chip labels. */
export const SERVER_ERROR_CODE_LABEL_I18N = {
  PURCHASE_DATE_REQUIRED:   "chipPurchaseDateRequired",
  INVALID_PURCHASE_DATE:    "chipInvalidPurchaseDate",
  PROCESS_DATE_REQUIRED:    "chipProcessDateRequired",
  INVALID_PROCESS_DATE:     "chipInvalidProcessDate",
  UNKNOWN_PROCESS_TYPE:     "chipUnknownProcessType",
  NO_ORDERS_FOR_DATE:       "chipNoOrdersForDate",
  PARCHMENT_INVALID:        "chipParchmentInvalid",
  PARCHMENT_EXCEEDS_CHERRY: "chipParchmentExceedsCherry",
  INVALID_DATA:             "chipInvalidData",
};

/** API error code → normalized field(s) it applies to (drives red highlight). */
export const SERVER_ERROR_CODE_FIELDS = {
  PURCHASE_DATE_REQUIRED:   ["purchaseDate"],
  INVALID_PURCHASE_DATE:    ["purchaseDate"],
  PROCESS_DATE_REQUIRED:    ["processDate"],
  INVALID_PROCESS_DATE:     ["processDate"],
  UNKNOWN_PROCESS_TYPE:     ["processType"],
  NO_ORDERS_FOR_DATE:       ["purchaseDate"],
  PARCHMENT_INVALID:        ["parchmentKg"],
  PARCHMENT_EXCEEDS_CHERRY: ["parchmentKg"],
  INVALID_DATA:             ["general"],
};

/** Normalized field → RAW header key sent to the update endpoint. */
export const RAW_KEY_BY_FIELD = {
  orderIds:      "orderIds",
  grade:         "grade",
  season:        "season",
  processDate:   "processDate",
  processType:   "processType",
  parchmentKg:   "parchmentKg",
  bags:          "bags",
  vehiclePlate:  "vehiclePlate",
  harvested:     "harvested",
  pulped:        "pulped",
  washed:        "washed",
  dryingStarted: "dryingStarted",
  movedToStore:  "movedToStore",
};

export function hasFieldError(record, field) {
  const md = (record && record.errorMetadata) || {};
  return Boolean(md[field] && md[field].length);
}

export function mapServerRecord(record) {
  const rec = record.recordedJsonData || {};
  const errorCodes = record.errorCodes || [];
  const isError =
    String(record.status).toUpperCase() === SERVER_RECORD_STATUS.ERROR_DETECTED;
  return {
    id: record.id,
    serverStatus: record.status,
    status: isError ? BULK_RECORD_STATUS.DATA_DISCREPANCY : BULK_RECORD_STATUS.SUCCESS,
    errorCodes,
    errorMetadata: record.errorMetadata || {},
    recordedJsonData: rec,
    rawPayload: record.payloadJsonData || null,
    processingBatchId: record.processingBatchId || null,
  };
}

export function mapServerImport(data = {}) {
  const records = (data.records || []).map(mapServerRecord);
  const successRecordsCount = records.filter(isSuccessRecord).length;
  const discrepancyRecordsCount = records.filter(isDataDiscrepancyRecord).length;
  return {
    bulkImportId: data.bulkImportId != null ? data.bulkImportId : data.id,
    status: data.status || null,
    buyingStationId: data.buyingStationId != null ? data.buyingStationId : null,
    originalFileName: data.originalFileName || null,
    downloadUrl: data.downloadUrl || null,
    createdAt: data.createdAt || null,
    totalRecordsCount: data.totalRecordsCount != null ? data.totalRecordsCount : records.length,
    errorRecordsCount: data.errorRecordsCount != null ? data.errorRecordsCount : discrepancyRecordsCount,
    successRecordsCount,
    failedRecordsCount: 0,
    discrepancyRecordsCount,
    importedRecordsCount: successRecordsCount,
    records,
    templateError: false,
  };
}

export function buildRecordUpdatePayload(record, overrides = {}) {
  const rec = (record && record.recordedJsonData) || {};
  const payload = {};
  Object.keys(RAW_KEY_BY_FIELD).forEach((field) => {
    const rawKey = RAW_KEY_BY_FIELD[field];
    const val = rec[field];
    // Preserve arrays (orderIds) as-is; null-coerce everything else.
    payload[rawKey] = Array.isArray(val) ? val : (val != null ? val : null);
  });
  return { ...payload, ...overrides };
}
